import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
} from "react";
import { type DragObjectWithType, type DragSourceMonitor, type XYCoord, useDrag, useDrop } from "react-dnd";

export interface CardProps {
  id: string | number;
  content: ReactNode;
  index: number; // Index of this Card component within the ListContainer.
  itemType: string;
  onHoverNewItem: (dragIndex: number, hoverIndex: number) => void; // Callback to run everytime a dragged item is hovered over other items.
  onDropItem: (dropTargetIndex: number) => void; // Callback to run when an item is dropped. Returns the item index where item was dropped.
}

const DRAG_HIDE_THRESHOLD_PX = 2;

export function Card(props: CardProps) {
  const ref = useRef<HTMLLIElement>(null);
  const justDroppedRef = useRef(false);
  const dropResetTimeout = useRef<number | null>(null);
  const shouldAllowDragRef = useRef(true);
  const [{ handlerId }, drop] = useDrop<CardProps & DragObjectWithType, any, any>({
    accept: props.itemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop(item) {
      props.onDropItem(item.index);
    },
    hover(draggedItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex = draggedItem.index; // Current position of the dragged item.
      const hoverIndex = props.index; // The position of the item hovered by the mouse.
      // Do nothing while user hasn't dragged the item anywhere.
      if (dragIndex === hoverIndex) {
        return;
      }
      // Swap the items within the list internal state so we can correctly render the list in order.
      props.onHoverNewItem(dragIndex, hoverIndex);
      // Move the dragged item to the current hover index in the DnD state.
      draggedItem.index = hoverIndex;
    },
  });

  const [{ isDragging, differenceFromInitialOffset }, drag] = useDrag<
    any,
    any,
    { isDragging: boolean; differenceFromInitialOffset: XYCoord | null }
  >({
    item: { type: props.itemType, id: props.id, index: props.index },
    collect(monitor: DragSourceMonitor) {
      return {
        isDragging: monitor.isDragging(),
        differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset(),
      };
    },
    canDrag: () => shouldAllowDragRef.current,
    begin() {
      justDroppedRef.current = false;
      if (dropResetTimeout.current !== null) {
        window.clearTimeout(dropResetTimeout.current);
        dropResetTimeout.current = null;
      }
    },
    end() {
      justDroppedRef.current = true;
      dropResetTimeout.current = window.setTimeout(() => {
        justDroppedRef.current = false;
        dropResetTimeout.current = null;
      }, 0);
    },
  });

  useEffect(() => {
    return () => {
      if (dropResetTimeout.current !== null) {
        window.clearTimeout(dropResetTimeout.current);
      }
    };
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLLIElement>) => {
    const container = ref.current;
    if (!container) {
      shouldAllowDragRef.current = true;
      return;
    }
    const target = event.target as HTMLElement;
    const handle = target.closest<HTMLElement>("[data-dnd-handle]");
    const hasAnyHandle = container.querySelector("[data-dnd-handle]") !== null;

    if (!hasAnyHandle) {
      shouldAllowDragRef.current = true;
      return;
    }

    shouldAllowDragRef.current = handle !== null;
  };

  const resetDragAllowance = () => {
    shouldAllowDragRef.current = true;
  };

  const handleClickCapture = (event: ReactMouseEvent) => {
    if (justDroppedRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const hasMovedEnough =
    !!differenceFromInitialOffset &&
    (Math.abs(differenceFromInitialOffset.x) >= DRAG_HIDE_THRESHOLD_PX ||
      Math.abs(differenceFromInitialOffset.y) >= DRAG_HIDE_THRESHOLD_PX);
  const opacity = isDragging && hasMovedEnough ? 0 : 1;
  drag(drop(ref));
  return (
    <li
      className={"draggable-item"}
      ref={ref}
      style={{ opacity }}
      data-handler-id={handlerId}
      onPointerDown={handlePointerDown}
      onPointerUp={resetDragAllowance}
      onPointerCancel={resetDragAllowance}
      onClickCapture={handleClickCapture}
    >
      {props.content}
    </li>
  );
}
